<template>
  <v-container>
    <!-- Título e Botões -->
    <v-row class="align-center mb-5">
      <v-col cols="auto">
        <h3 class="page-title">
          <v-icon color="primary" class="mr-2">mdi-stethoscope</v-icon>
          Atendimentos
        </h3>
      </v-col>

      <v-spacer></v-spacer>

      <v-col cols="auto">
        <!-- Botão para reabrir o tour desta aba -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="primary"
              v-bind="attrs"
              v-on="on"
              aria-label="Ver o tour desta aba"
              class="mr-3"
              @click="startTour"
            >
              <v-icon>mdi-help-circle-outline</v-icon>
            </v-btn>
          </template>
          <span>Ver o tour desta aba</span>
        </v-tooltip>

        <!-- Botão para atualizar a lista -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              color="primary"
              v-bind="attrs"
              v-on="on"
              :loading="loading"
              class="mr-3"
              @click="loadAppointments"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Atualizar lista</span>
        </v-tooltip>

        <!-- Botão para adicionar consulta -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              color="primary"
              v-bind="attrs"
              v-on="on"
              @click="openAppointmentDialog()"
              class="mr-3"
              data-tour="ap-add"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>Adicionar atendimento</span>
        </v-tooltip>

        <!-- Botão para buscar -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              color="primary"
              v-bind="attrs"
              v-on="on"
              @click="openSearchDialog"
              class="mr-3"
            >
              <v-icon>mdi-magnify</v-icon>
            </v-btn>
          </template>
          <span>Buscar atendimentos</span>
        </v-tooltip>

        <!-- Botão de ordenação -->
        <v-menu v-model="menuOpen" :close-on-content-click="false" offset-y>
          <template v-slot:activator="{ on: menu, attrs }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn icon color="primary" v-bind="attrs" v-on="{ ...menu, ...tooltip }">
                  <v-icon>mdi-sort</v-icon>
                </v-btn>
              </template>
              <span>Ordenar atendimentos</span>
            </v-tooltip>
          </template>

          <v-list>
            <v-list-item @click="sortAppointments('asc')">
              <v-list-item-title>Crescente</v-list-item-title>
            </v-list-item>
            <v-list-item @click="sortAppointments('desc')">
              <v-list-item-title>Decrescente</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
    </v-row>

    <!-- Tabela de Consultas -->
    <v-data-table
      :headers="headers"
      :items="appointments"
      item-key="id"
      class="elevation-1"
      data-tour="ap-table"
      :loading="loading"
      loading-text="Carregando..."
      :no-data-text="'Nenhum registro encontrado'"
      :footer-props="{
        itemsPerPageText: 'Itens por página:',
        pageText: '{0}-{1} de {2}',
      }"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td>{{ formatDate(item.appointment_date) }}</td>
          <td>{{ formatDate(item.updated_at) }}</td>
          <td>{{ formatTime(item.start_time) }}</td>
          <td>{{ formatTime(item.end_time) }}</td>
          <td>{{ item.reason || "-" }}</td>

          <!-- Transcrição -->
          <td class="text-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" style="display: inline-block">
                  <v-btn
                    icon
                    small
                    color="teal"
                    @click="openDialog('transcription', item)"
                    :disabled="!item.transcription"
                  >
                    <v-icon small>mdi-file-document</v-icon>
                  </v-btn>
                </div>
              </template>
              <span>{{
                item.transcription
                  ? "Ver transcrição"
                  : "Sem transcrição disponível"
              }}</span>
            </v-tooltip>
          </td>

          <!-- Notas -->
          <td class="text-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" style="display: inline-block">
                  <v-btn
                    icon
                    small
                    color="deep-purple"
                    @click="openDialog('notes', item)"
                    :disabled="!item.notes || item.notes.length === 0"
                  >
                    <v-icon small>mdi-note-text</v-icon>
                  </v-btn>
                </div>
              </template>
              <span>{{
                item.notes && item.notes.length > 0
                  ? "Ver notas"
                  : "Sem notas disponíveis"
              }}</span>
            </v-tooltip>
          </td>

          <!-- Resumo Estruturado -->
          <td class="text-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" style="display: inline-block">
                  <v-btn
                    icon
                    small
                    color="indigo"
                    @click="openDialog('summary', item)"
                    :disabled="!item.structured_summary"
                  >
                    <v-icon small>mdi-text-box-outline</v-icon>
                  </v-btn>
                </div>
              </template>
              <span>{{
                item.structured_summary
                  ? "Ver resumo estruturado"
                  : "Sem resumo disponível"
              }}</span>
            </v-tooltip>
          </td>

          <!-- Ações -->
          <td class="text-center actions-cell">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click="editAppointment(item)"
                  class="mr-1"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Editar atendimento</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="error"
                  v-bind="attrs"
                  v-on="on"
                  @click="deleteAppointment(item)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Excluir atendimento</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- Dialog: Transcrição -->
    <v-dialog v-model="dialogTranscription" max-width="700px" scrollable>
      <v-card class="ds-dialog">
        <v-card-title class="ds-dialog-header">
          <div class="header-text">
            <span class="ds-dialog-title">Transcrição da Consulta</span>
            <span v-if="consultaContexto" class="header-subtitle">{{ consultaContexto }}</span>
          </div>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                dark
                class="close-btn"
                v-bind="attrs"
                v-on="on"
                @click="dialogTranscription = false"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
            <span>Fechar</span>
          </v-tooltip>
        </v-card-title>
        <v-card-text ref="transcriptBox" class="ds-dialog-body transcription-content">
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-microphone-outline</v-icon>
                <span>Transcrição</span>
              </div>
              <span v-if="transcriptWordCount" class="ds-form-section__aside">
                {{ transcriptWordCount }} {{ transcriptWordCount === 1 ? "palavra" : "palavras" }}
              </span>
            </div>
            <div v-if="selectedItem && selectedItem.transcription">
              <!-- Parte do texto já escapado (transcriptHtml): o que vira marcação é só
                   o <mark> do trecho, os parágrafos e o destaque das notas. -->
              <div class="transcription-text" v-html="transcriptParagraphsHtml"></div>
            </div>
            <div v-else class="text-center text--secondary">
              Nenhuma transcrição disponível
            </div>
          </section>
        </v-card-text>
        <v-card-actions class="ds-dialog-actions">
          <v-btn outlined color="primary" @click="dialogTranscription = false">
            <v-icon left>mdi-close</v-icon>
            Fechar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            depressed
            @click="copyToClipboard(selectedItem.transcription)"
          >
            <v-icon left>mdi-content-copy</v-icon>
            Copiar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Notas -->
    <v-dialog v-model="dialogNotes" max-width="700px" scrollable>
      <v-card class="ds-dialog">
        <v-card-title class="ds-dialog-header">
          <div class="header-text">
            <span class="ds-dialog-title">Notas da Consulta</span>
            <span v-if="consultaContexto" class="header-subtitle">{{ consultaContexto }}</span>
          </div>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                dark
                class="close-btn"
                v-bind="attrs"
                v-on="on"
                @click="dialogNotes = false"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
            <span>Fechar</span>
          </v-tooltip>
        </v-card-title>
        <v-card-text class="ds-dialog-body">
          <section
            v-if="
              selectedItem &&
              selectedItem.notes &&
              selectedItem.notes.length > 0
            "
            class="ds-form-section ds-form-section--flush"
          >
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-note-text-outline</v-icon>
                <span>Notas</span>
              </div>
              <span class="ds-form-section__aside">{{ selectedItem.notes.length }}</span>
            </div>
            <!-- Linha do tempo: hora à esquerda, nota à direita. -->
            <ol class="notes-timeline">
              <li
                v-for="note in selectedItem.notes"
                :key="note.id"
                class="notes-timeline__item"
              >
                <span class="notes-timeline__time">{{ formatTime(note.created_at) }}</span>
                <!-- SALVIA-881: Nota → Resumo → Conversa. A nota abre o resumo
                     na frase que a contém. -->
                <div
                  class="notes-timeline__body"
                  :class="{ 'note-link': hasSummary }"
                  :role="hasSummary ? 'button' : null"
                  :tabindex="hasSummary ? 0 : null"
                  @click="hasSummary && showNoteInSummary(note)"
                  @keydown.enter.prevent="hasSummary && showNoteInSummary(note)"
                >
                  <p class="notes-timeline__content">{{ note.content }}</p>
                  <!-- A hora já está na coluna da esquerda; aqui só o que muda. -->
                  <div v-if="note.is_private" class="notes-timeline__meta">
                    <v-chip x-small color="orange" text-color="white">Privada</v-chip>
                  </div>
                </div>
                <v-tooltip v-if="hasSummary" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      v-bind="attrs"
                      v-on="on"
                      aria-label="Ver no resumo"
                      @click="showNoteInSummary(note)"
                    >
                      <v-icon small>mdi-arrow-down-circle-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Ver no resumo</span>
                </v-tooltip>
              </li>
            </ol>
          </section>
          <section v-else class="ds-form-section text-center text--secondary">
            Nenhuma nota disponível
          </section>
        </v-card-text>
        <v-card-actions class="ds-dialog-actions">
          <v-btn outlined color="primary" @click="dialogNotes = false">
            <v-icon left>mdi-close</v-icon>
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Resumo Estruturado -->
    <v-dialog v-model="dialogSummary" max-width="800px" scrollable>
      <v-card class="ds-dialog">
        <v-card-title class="ds-dialog-header">
          <div class="header-text">
            <span class="ds-dialog-title">Resumo Estruturado</span>
            <span v-if="consultaContexto" class="header-subtitle">{{ consultaContexto }}</span>
          </div>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                dark
                class="close-btn"
                v-bind="attrs"
                v-on="on"
                @click="dialogSummary = false"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
            <span>Fechar</span>
          </v-tooltip>
        </v-card-title>
        <!-- salvia-841: Completo / Resumido. salvia-937: o resumido vem em
             tópicos (short_summary); sem ele, deriva do completo. -->
        <v-tabs
          v-if="selectedItem && selectedItem.structured_summary"
          v-model="abaResumo"
          grow
          height="40"
          color="primary"
        >
          <v-tab>
            <v-icon small left>mdi-text-box-outline</v-icon>
            Completo
          </v-tab>
          <v-tab>
            <v-icon small left>mdi-text-short</v-icon>
            Resumido
          </v-tab>
        </v-tabs>
        <v-card-text class="ds-dialog-body summary-content">
          <section
            v-if="abaResumo === 1 && selectedItem && selectedItem.structured_summary && !resumoResumido"
            class="ds-form-section text-center text--secondary"
          >
            Nenhuma seção com conteúdo — veja a versão completa.
          </section>
          <template v-else-if="selectedItem && selectedItem.structured_summary">
            <p v-if="summaryIsLinked" class="summary-hint">
              <v-icon x-small class="mr-1">mdi-cursor-default-click-outline</v-icon>
              Clique numa frase para ver o trecho da conversa.
            </p>
            <!-- Cada seção (título + texto) num cartão; vazias ficam ocultas.
                 O par h4 + p continua lado a lado dentro do cartão — é por ele
                 que showNoteInSummary acha o título da frase. -->
            <div
              ref="summaryBody"
              class="summary-text summary-cards"
              :class="{
                'summary-linkable': summaryIsLinked,
                'summary-cards--show-empty': showEmptySections,
              }"
              v-html="summaryCardsHtml"
              @click="openTranscriptPassage"
              @keydown.enter.prevent="openTranscriptPassage"
            ></div>
            <!-- Seções "não mencionado" ficam recolhidas, mas nunca somem sem
                 aviso: com todas vazias, a tela ficava em branco. -->
            <p
              v-if="summaryCounts.total && summaryCounts.empty === summaryCounts.total && !showEmptySections"
              class="summary-all-empty"
            >
              Nenhuma informação clínica foi identificada nesta consulta: todas
              as seções vieram como "não mencionado".
            </p>
            <button
              v-if="summaryCounts.empty"
              type="button"
              class="summary-empty-toggle"
              @click="showEmptySections = !showEmptySections"
            >
              {{ showEmptySections ? "Ocultar" : "Mostrar" }}
              {{ summaryCounts.empty }}
              {{ summaryCounts.empty === 1 ? "seção sem informação" : "seções sem informação" }}
            </button>
          </template>
          <section v-else class="ds-form-section text-center text--secondary">
            Nenhum resumo disponível
          </section>
        </v-card-text>
        <v-card-actions class="ds-dialog-actions">
          <v-btn outlined color="primary" @click="dialogSummary = false">
            <v-icon left>mdi-close</v-icon>
            Fechar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            depressed
            @click="copyToClipboard(textoDoResumoDaAba)"
          >
            <v-icon left>mdi-content-copy</v-icon>
            Copiar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Busca -->
    <v-dialog v-model="searchDialog" max-width="500px" scrollable>
      <v-card class="ds-dialog">
        <v-card-title class="ds-dialog-header">
          <span class="ds-dialog-title">Buscar Consultas</span>
        </v-card-title>
        <v-card-text class="ds-dialog-body">
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-magnify</v-icon>
                <span>Busca</span>
              </div>
            </div>
            <v-text-field
              v-model="searchQuery"
              label="Digite para buscar..."
              outlined
              dense
              clearable
              hide-details="auto"
              @keyup.enter="performSearch"
            ></v-text-field>
          </section>
        </v-card-text>
        <v-card-actions class="ds-dialog-actions">
          <v-btn outlined color="primary" @click="searchDialog = false">
            <v-icon left>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" depressed @click="performSearch"> Buscar </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import http from "@/services/http";
import { useSnackbar } from "@/composables/useSnackbar";
import tourMixin from "@/features/tour/tourMixin";
import { formatDate, formatDateTime, formatTime } from "@/utils/datetime";
import { sanitizeRichHtml } from "@/utils/sanitizeHtml";
import {
  formatSummaryHtml,
  htmlToNumberedText,
  shortSummaryToDisplay,
  isEmptySection,
  parseSummarySections,
} from "@/features/patients/appointments/utils/consultationSummary";
import {
  bestMatchingSentence,
  findTranscriptPassage,
  transcriptHtml,
} from "@/features/patients/appointments/utils/transcriptLink";

export default {
  name: "Appointments",

  mixins: [tourMixin],

  setup() {
    const { success, error, warning, info, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, info, erroDeRequisicao };
  },

  computed: {
    tourId() {
      return "sec-atendimentos-pg";
    },

    /**
     * O resumo é renderizado com v-html porque a formatação (títulos e
     * parágrafos) faz parte do documento. Mas o conteúdo vem da IA sobre a
     * transcrição e é gravado como `nullable|string` — conteúdo de fora.
     *
     * Por isso não dá pra escapar (destruiria a formatação) nem confiar: passa
     * por whitelist, que mantém as tags de texto e zera TODOS os atributos.
     * Some qualquer `onerror`/`onclick`/`style`/`href` de uma vez, inclusive nos
     * resumos antigos que já estão gravados no banco com HTML arbitrário.
     */
    /** salvia-841: texto numerado da completa, a partir do HTML gravado. */
    resumoCompleto() {
      return htmlToNumberedText(this.selectedItem?.structured_summary || "");
    },

    resumoResumido() {
      return shortSummaryToDisplay(this.selectedItem?.short_summary, this.resumoCompleto);
    },

    /** O que o "Copiar" leva: a versão da aba aberta (0 = completa). */
    textoDoResumoDaAba() {
      return this.abaResumo === 1 ? this.resumoResumido : this.resumoCompleto;
    },

    hasTranscription() {
      return Boolean(this.selectedItem?.transcription?.trim());
    },

    /** Resumo com conteúdo para onde as notas possam levar (SALVIA-881). */
    hasSummary() {
      return parseSummarySections(this.resumoCompleto).some((s) => !isEmptySection(s.content));
    },

    /**
     * SALVIA-881: resumo montado do TEXTO (escapado em `formatSummaryHtml`),
     * com cada frase num `<span>` — alvo das notas — e, havendo transcrição,
     * clicável para ela. Resumo legado sem as seções numeradas devolve "" e
     * segue pelo HTML gravado.
     */
    linkedSummary() {
      const text = this.abaResumo === 1 ? this.resumoResumido : this.resumoCompleto;
      return formatSummaryHtml(text, { linkable: this.hasTranscription, sentences: true });
    },

    summaryIsLinked() {
      return this.hasTranscription && Boolean(this.linkedSummary);
    },

    /** A transcrição escapada, com o trecho da frase clicada marcado. */
    transcriptDisplayHtml() {
      return transcriptHtml(this.selectedItem?.transcription, this.transcriptPassage);
    },

    /** Data · horário · motivo da consulta aberta — subtítulo das telinhas. */
    consultaContexto() {
      const a = this.selectedItem;
      if (!a) return "";
      const ini = a.start_time ? formatTime(a.start_time, { fallback: "" }) : "";
      const fim = a.end_time ? formatTime(a.end_time, { fallback: "" }) : "";
      const horario = ini && fim ? `${ini}–${fim}` : ini;
      return [a.appointment_date && formatDate(a.appointment_date, { fallback: "" }), horario, a.reason]
        .filter(Boolean)
        .join(" · ");
    },

    transcriptWordCount() {
      const t = this.selectedItem?.transcription || "";
      return (t.match(/\S+/g) || []).length;
    },

    /**
     * A transcrição em parágrafos (linha em branco separa) e com as notas
     * inseridas na fala (`[Nota: …]`, SALVIA-881) destacadas. Parte do HTML
     * já escapado por transcriptHtml — nada do texto vira marcação.
     */
    transcriptParagraphsHtml() {
      return this.transcriptDisplayHtml
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter(Boolean)
        .map((p) => {
          const body = p
            .replace(/\[Nota: ([^\]]*)\]/g, '<span class="transcript-note"><b>Nota</b> $1</span>')
            .replace(/\n/g, "<br>");
          return `<p>${body}</p>`;
        })
        .join("");
    },

    /**
     * O resumo (já saneado em safeSummary) com cada seção — h4 + p — dentro de
     * um cartão. Seção "não mencionado" (parágrafo --muted) ganha a classe
     * --empty e fica oculta. Resumo legado fora desse formato passa intacto.
     */
    summaryCardsHtml() {
      return this.safeSummary.replace(
        /(<h4 class="summary-heading">[\s\S]*?<\/h4>)(<p class="summary-paragraph( summary-paragraph--muted)?">[\s\S]*?<\/p>)?/g,
        (m, h, p, muted) =>
          `<section class="summary-card${muted || !p ? " summary-card--empty" : ""}">${h}${p || ""}</section>`
      );
    },

    /** Seções do resumo na tela: total e quantas são "não mencionado". */
    summaryCounts() {
      const html = this.summaryCardsHtml || "";
      return {
        total: (html.match(/<section class="summary-card/g) || []).length,
        empty: (html.match(/summary-card--empty/g) || []).length,
      };
    },

    safeSummary() {
      if (this.linkedSummary) return this.linkedSummary;
      // A completa segue exibindo o HTML gravado; a resumida é montada aqui,
      // e passa pelo MESMO saneamento — uma porta só para o v-html.
      const html =
        this.abaResumo === 1
          ? formatSummaryHtml(this.resumoResumido)
          : this.selectedItem?.structured_summary;
      return sanitizeRichHtml(html, {
        allowedClasses: new Set([
          "summary-heading",
          "summary-paragraph",
          "summary-paragraph--muted",
        ]),
      });
    },
  },

  data() {
    return {
      appointments: [],
      loading: false,
      menuOpen: false,
      searchDialog: false,
      dialogTranscription: false,
      dialogNotes: false,
      dialogSummary: false,
      abaResumo: 0,
      showEmptySections: false,
      selectedItem: null,
      // SALVIA-881: trecho da transcrição aberto a partir do resumo.
      transcriptPassage: null,
      appointmentToDelete: null,
      searchQuery: "",
      isDeleting: false,
      allAppointments: [],

      headers: [
        { text: "Data",        value: "appointment_date" },
        { text: "Atualizado",  value: "updated_at" },
        { text: "Início",      value: "start_time" },
        { text: "Fim",         value: "end_time" },
        { text: "Motivo",      value: "reason" },
        { text: "Transcrição", value: "transcription", sortable: false, align: "center" },
        { text: "Notas",       value: "notes",         sortable: false, align: "center" },
        { text: "Resumo",      value: "summary",       sortable: false, align: "center" },
        { text: "Ações",       value: "actions",       sortable: false, align: "center" },
      ],
    };
  },

  mounted() {
    this.loadAppointments();
    this.$root.$on("reload-appointments", this.loadAppointments);
  },

  beforeDestroy() {
    this.$root.$off("reload-appointments", this.loadAppointments);
  },

  methods: {
    formatDate: (v) => formatDate(v, { fallback: "-" }),
    formatTime: (v) => formatTime(v, { fallback: "-" }),
    formatDateTime: (v) => formatDateTime(v, { fallback: "-" }),
    tourReady() {
      return !this.loading;
    },
    tourSteps() {
      return [
        {
          title: "Atendimentos 🩺",
          description:
            "Esta seção reúne o histórico de atendimentos/consultas do paciente ao longo do tempo.",
        },
        {
          anchor: "ap-add",
          title: "Adicionar atendimento",
          description:
            "Registra um novo atendimento do paciente — data, horário, motivo e a evolução do encontro. Também há botões para buscar e ordenar a lista ao lado.",
        },
        {
          anchor: "ap-table",
          title: "Histórico de atendimentos",
          description:
            "Cada linha é um atendimento, com data, horário e motivo. Nas ações da linha você acessa a transcrição, visualiza, edita ou remove o registro.",
        },
      ];
    },

    async loadAppointments() {
      this.loading = true;

      const patientId = this.$route.params.id;
      if (!patientId) {
        this.error("ID do paciente não encontrado");
        this.loading = false;
        return;
      }

      try {
        const { data } = await http.get(
          `/api/appointments/patient/${patientId}`,
        );

        this.appointments = data.data.sort((a, b) => {
          const dateTimeA = new Date(
            `${a.appointment_date}T${a.start_time || "00:00:00"}`,
          );
          const dateTimeB = new Date(
            `${b.appointment_date}T${b.start_time || "00:00:00"}`,
          );
          return dateTimeB - dateTimeA;
        });
        this.allAppointments = [...this.appointments];

        if (this.appointments.length === 0) {
          this.warning("Sem registro de atendimentos para este paciente!");
        } else {
          this.success(
            `${this.appointments.length} ${
              this.appointments.length === 1
                ? "atendimento carregado"
                : "atendimentos carregados"
            } com sucesso`,
          );
        }
      } catch (err) {
        console.error("Erro ao carregar consultas:", err);
        this.erroDeRequisicao(err, "Erro ao carregar consultas");
      } finally {
        this.loading = false;
      }
    },

    openDialog(type, item) {
      this.selectedItem = item;
      this.transcriptPassage = null;

      switch (type) {
        case "transcription":
          this.dialogTranscription = true;
          break;
        case "notes":
          this.dialogNotes = true;
          break;
        case "summary":
          this.abaResumo = 0; // abre sempre na completa
          this.showEmptySections = false;
          this.dialogSummary = true;
          break;
      }
    },

    /**
     * SALVIA-881 — clique numa frase do resumo: abre a transcrição por cima,
     * já no trecho de onde a frase saiu. Sem trecho confiável, diz isso.
     */
    openTranscriptPassage(event) {
      const link = event.target?.closest?.(".summary-link");
      if (link) this.showTranscriptPassage(link.textContent);
    },

    /**
     * SALVIA-881 — nota → resumo: abre o resumo (aba Completa) por cima e faz
     * piscar a frase que corresponde à nota.
     */
    showNoteInSummary(note) {
      if (!this.hasSummary) return;
      this.abaResumo = 0;
      this.showEmptySections = false;
      this.dialogSummary = true;
      // Mesmo cuidado da transcrição: o diálogo monta ao abrir e anima.
      this.$nextTick(() => {
        setTimeout(() => {
          const body = this.$refs.summaryBody;
          const spans = body ? Array.from(body.querySelectorAll(".summary-sentence")) : [];
          const texts = spans.map((span) => {
            const heading = span.closest("p")?.previousElementSibling;
            return `${heading?.tagName === "H4" ? heading.textContent : ""} ${span.textContent}`;
          });
          const index = bestMatchingSentence(texts, note.content);
          if (index < 0) {
            this.info("Não encontrei esta nota no resumo.");
            return;
          }
          const el = spans[index];
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.remove("summary-sentence--flash");
          void el.offsetWidth;
          el.classList.add("summary-sentence--flash");
          setTimeout(() => el.classList.remove("summary-sentence--flash"), 2600);
        }, 250);
      });
    },

    /** Abre a transcrição no trecho de onde a frase do resumo saiu. */
    showTranscriptPassage(text) {
      if (!this.hasTranscription) return;

      const passage = findTranscriptPassage(this.selectedItem.transcription, text);
      if (!passage) {
        this.info("Não encontrei esse trecho na transcrição.");
        return;
      }

      this.transcriptPassage = passage;
      this.dialogTranscription = true;
      // O diálogo monta o conteúdo só ao abrir e anima a entrada: espera ele
      // existir antes de rolar.
      this.$nextTick(() => {
        setTimeout(() => {
          const box = this.$refs.transcriptBox?.$el;
          box?.querySelector("mark.transcript-passage")?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 200);
      });
    },

    openAppointmentDialog() {
      this.$emit("open-appointment-dialog", null);
    },

    editAppointment(item) {
      this.$emit("open-appointment-dialog", item.id);
    },

    async deleteAppointment(item) {
      this.appointmentToDelete = item;
      const ok = await this.$confirm({
        type: "delete",
        title: "Confirmar exclusão",
        message: "Tem certeza que deseja excluir este atendimento?",
        hint: "Esta ação não pode ser desfeita.",
      });
      if (!ok) {
        this.appointmentToDelete = null;
        return;
      }
      await this.confirmDelete();
    },

    async confirmDelete() {
      if (!this.appointmentToDelete) return;

      this.isDeleting = true;

      try {
        await http.delete(`/api/appointments/${this.appointmentToDelete.id}`);

        this.success("Consulta excluída com sucesso!");
        this.appointmentToDelete = null;
        await this.loadAppointments();
      } catch (err) {
        console.error("Erro ao excluir consulta:", err);
        this.erroDeRequisicao(err, "Erro ao excluir consulta");
      } finally {
        this.isDeleting = false;
      }
    },

    openSearchDialog() {
      this.searchDialog = true;
    },

    performSearch() {
      if (!this.searchQuery) {
        this.appointments = [...this.allAppointments];
        this.searchDialog = false;
        return;
      }

      const filtered = this.allAppointments.filter((item) => {
        return Object.values(item).some((val) => {
          if (val === null || val === undefined) return false;
          return String(val)
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase());
        });
      });

      this.appointments = filtered;
      this.searchDialog = false;

      if (filtered.length === 0) {
        this.info("Nenhum resultado encontrado");
      } else {
        this.success(`${filtered.length} resultado(s) encontrado(s)`);
      }
    },

    sortAppointments(order) {
      this.menuOpen = false;

      this.appointments.sort((a, b) => {
        const dateA = new Date(a.appointment_date);
        const dateB = new Date(b.appointment_date);
        return order === "asc" ? dateA - dateB : dateB - dateA;
      });

      this.info(
        `Ordenado em ordem ${order === "asc" ? "crescente" : "decrescente"}`,
      );
    },

    copyToClipboard(text) {
      if (!text) {
        this.warning("Nenhum conteúdo para copiar");
        return;
      }

      navigator.clipboard.writeText(text);
      this.success("Copiado para a área de transferência!");
    },
  },
};
</script>

<style scoped>
.v-data-table td {
  white-space: nowrap;
}

/* ═══ Tabela padronizada (estilo BodyCompositionDialog / Goals) ═══ */
.v-data-table >>> thead th,
.v-simple-table >>> thead th {
  background-color: var(--ds-blue-50) !important;
  border-bottom: 2px solid var(--ds-blue-100) !important;
  padding: 12px !important;
  font-size: 10px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.4px !important;
  color: var(--ds-primary) !important;
  font-weight: 700 !important;
  height: auto !important;
}

.v-data-table >>> tbody tr,
.v-simple-table >>> tbody tr {
  transition: background-color 0.15s ease;
}

.v-data-table >>> tbody tr:hover,
.v-simple-table >>> tbody tr:hover {
  background-color: var(--ds-blue-50) !important;
}

.v-data-table >>> tbody td,
.v-simple-table >>> tbody td {
  border-bottom: 1px solid var(--ds-blue-divider) !important;
  font-size: 12px !important;
  color: var(--ds-text-600) !important;
}

/* ═══ Célula de ações ═══ */
.actions-cell {
  white-space: nowrap;
  min-width: 96px;
}

/* ═══ Cabeçalho das telinhas: título + contexto da consulta ═══ */
.header-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.25;
  position: relative;
  z-index: 1;
}
.header-subtitle {
  font-size: 12px;
  font-weight: 400;
  opacity: 0.88;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ═══ Transcrição: texto corrido de leitura, em parágrafos ═══ */
.transcription-text {
  font-size: 14.5px;
  line-height: 1.75;
  color: var(--ds-text);
  word-wrap: break-word;
}
.transcription-text >>> p {
  margin: 0 0 12px;
}
.transcription-text >>> p:last-child {
  margin-bottom: 0;
}
/* Nota escrita durante a fala (SALVIA-881): destacada, não confundida com fala. */
.transcription-text >>> .transcript-note {
  display: inline-block;
  margin: 2px 0;
  padding: 1px 8px;
  border-radius: 4px;
  background: var(--ds-primary-surface);
  border: 1px solid var(--ds-primary-border);
  color: var(--ds-primary-deep);
  font-size: 13px;
  line-height: 1.6;
}
.transcription-text >>> .transcript-note b {
  margin-right: 4px;
  font-size: 10px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

/* ═══ Resumo: uma seção por cartão, título na faixa (padrão ds-form-section) ═══ */
.summary-hint {
  display: flex;
  align-items: center;
  margin: 0 0 8px;
  font-size: 12px;
  color: var(--ds-text-soft);
}
.summary-cards >>> .summary-card {
  background: var(--ds-surface-white);
  border: 1px solid var(--ds-blue-100);
  border-radius: 8px;
  overflow: hidden;
}
.summary-cards >>> .summary-card + .summary-card {
  margin-top: 8px;
}
.summary-cards >>> .summary-card--empty {
  display: none;
}
.summary-cards--show-empty >>> .summary-card--empty {
  display: block;
  opacity: 0.75;
}
.summary-all-empty {
  margin: 0 0 8px;
  padding: 10px 12px;
  font-size: 13px;
  color: var(--ds-text-soft);
  background: var(--ds-surface-white);
  border: 1px solid var(--ds-blue-100);
  border-radius: 8px;
}
.summary-empty-toggle {
  margin-top: 6px;
  padding: 2px 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--ds-primary);
  background: none;
  border: 0;
  cursor: pointer;
}
.summary-empty-toggle:hover {
  text-decoration: underline;
}
.summary-cards >>> h4 {
  margin: 0;
  padding: 6px 10px;
  background: var(--ds-blue-50);
  border-bottom: 1px solid var(--ds-blue-100);
  color: var(--ds-primary);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
}
/* pre-line: itens de lista vêm em linhas (salvia-841) */
.summary-cards >>> p {
  margin: 0;
  padding: 8px 10px 10px;
  white-space: pre-line;
  font-size: 14.5px;
  line-height: 1.7;
  color: var(--ds-text);
}
/* Resumo legado fora do formato numerado: sem cartões, só o texto. */
.summary-cards >>> ul,
.summary-cards >>> ol {
  margin: 8px 0 12px 20px;
  line-height: 1.7;
}
.summary-cards >>> strong {
  color: var(--ds-primary);
  font-weight: 600;
}

/* ═══ Notas: linha do tempo ═══ */
.notes-timeline {
  list-style: none;
  margin: 0;
  padding: 4px 0 !important; /* vence o padding-left de ol do Vuetify */
}
.notes-timeline__item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--ds-blue-100);
}
.notes-timeline__item:last-child {
  border-bottom: 0;
}
.notes-timeline__time {
  flex: 0 0 auto;
  min-width: 44px;
  padding-top: 2px;
  font-size: 12px;
  font-weight: 700;
  color: var(--ds-primary);
  font-variant-numeric: tabular-nums;
}
.notes-timeline__body {
  flex: 1 1 auto;
  min-width: 0;
}
.notes-timeline__content {
  margin: 0 !important; /* vence a margem de p do Vuetify */
  font-size: 14px;
  line-height: 1.6;
  color: var(--ds-text);
  white-space: pre-line;
  word-wrap: break-word;
}
.notes-timeline__meta {
  margin-top: 4px;
  display: flex;
  align-items: center;
  font-size: 11px;
  color: var(--ds-text-muted);
}

/* ═══ Page title ═══ */
.page-title {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 700;
  color: var(--ds-text-strong);
  letter-spacing: -0.3px;
  margin: 0;
}

/* ═══ Design system dialog ═══ */
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}
.ds-dialog-header--danger {
  background: linear-gradient(135deg, var(--ds-danger) 0%, var(--ds-danger-mid) 60%, var(--ds-danger-light) 100%) !important;
}

/* SALVIA-881: frases do resumo que levam ao trecho da transcrição */
.summary-text >>> .summary-sentence {
  border-radius: 3px;
  transition: background-color 0.15s;
}
.summary-text >>> .summary-link {
  cursor: pointer;
}
.summary-text >>> .summary-link:hover,
.summary-text >>> .summary-link:focus-visible {
  background-color: rgba(var(--v-primary-base-rgb, 60, 110, 90), 0.12);
  outline: none;
}
.transcription-text >>> mark.transcript-passage {
  background-color: rgba(255, 213, 79, 0.45);
  color: inherit;
  border-radius: 3px;
  padding: 1px 0;
}
.transcription-text >>> mark.transcript-passage strong {
  font-weight: 700;
}

/* SALVIA-881: frase do resumo que corresponde à nota — pisca e some */
.summary-text >>> .summary-sentence--flash {
  animation: summary-sentence-flash 2.6s ease-out;
}
@keyframes summary-sentence-flash {
  0%,
  35% {
    background-color: rgba(255, 213, 79, 0.75);
    box-shadow: 0 0 0 2px rgba(255, 213, 79, 0.75);
  }
  100% {
    background-color: transparent;
    box-shadow: 0 0 0 2px transparent;
  }
}

/* SALVIA-881: nota que leva à frase do resumo */
.note-link {
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.15s;
}
.note-link:hover,
.note-link:focus-visible {
  background-color: rgba(60, 110, 90, 0.08);
  outline: none;
}
</style>
